import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { LicenseInfo } from "@mui/x-license-pro";
import { useQueryClient } from "@tanstack/react-query";
import mixpanel from "mixpanel-browser";
import { useContext, useEffect, useState } from "react";
import { ProSidebarProvider } from "react-pro-sidebar";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import useAPI from "./api/hooks/useAPI";
import { useCheckUserPermission } from "./api/hooks/useCheckUserPermission";
import useMSSP from "./api/hooks/useMSSP";
import { controlKeys } from "./api/queryKeys";
import "./App.css";
import MSSPHeader from "./components/mssp/MSSPHeader";
import IntegrationSuccessModal from "./components/onboarding/IntegrationSuccessModal";
import Onboarding from "./components/onboarding/Onboarding";
import OnboardingModal from "./components/onboarding/OnboardingModal";
import OnboardingAutoOpenWrapper from "./components/onboarding/OnboardingProvider";
import AppSidebar from "./components/structure/AppSidebar";
import getAppScenes from "./components/structure/Navigation";
import ToastContainer, { Toast } from "./components/Toast";
import { BootstrapContext } from "./contexts/BootstrapContext";
import { CustomerFeatureFlagsProvider } from "./contexts/CustomerFeatureFlagsContext";
import { EnvFeaturesProvider } from "./contexts/EnvFeaturesContext";
import { NotificationContext } from "./contexts/NotificationContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { POCProvider } from "./contexts/POCContext";
import ComponentDocs from "./documentation/ComponentDocs";
import AIDashboard from "./scenes/dashboard/ai_posture/AIDashboard";
import FrameworksView from "./scenes/posture/frameworks/FrameworksView";
import StepsView from "./scenes/posture/steps/StepsView";
import OnPremConfigurationView from "./scenes/settings/connector/OnPremConfigurationView";
import ApiDocs from "./scenes/settings/organization/api/ApiDocs";
import SupportDevTools from "./scenes/settings/support/SupportDevTools";
import IntegrationPage from "./scenes/stack/integrations/components/IntegrationPage";
import HandleGitHubRedirect from "./scenes/stack/integrations/GitHub/RedirectGitHub";
import { ColorModeContext, useMode } from "./theme";
import {
  PLATFORM_USER_ACTIONS,
  QueryParam,
  TaskStatus,
} from "./utils/constants";
import { getEnvStorageKey } from "./utils/environment";

// MUI Pro license
LicenseInfo.setLicenseKey(
  "41fe740b84e746a720cbaf3398bdcc7aTz0xMTc4MTYsRT0xNzg3MDk3NTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI="
);

const TOPBAR_STORAGE_KEY = getEnvStorageKey("selectedTopbarIndex");

function App() {
  const [theme, colorMode] = useMode();
  const [selectedTopbarIndex, setSelectedTopbarIndex] = useState(() => {
    const storedSidebarItems = localStorage.getItem(TOPBAR_STORAGE_KEY);
    return storedSidebarItems ? JSON.parse(storedSidebarItems) : 0;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [scanFinished, setScanFinished] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [lastScanTime, setLastScanTime] = useState(null);
  const queryClient = useQueryClient();
  const intervalTimeInMilliseconds = 10000; // 10 seconds
  const delayBetweenToasts = 2000; // 2 seconds
  const location = useLocation();
  const { trackEvent, getScanStatus } = useAPI();
  const MIXPANEL_PERCENTAGE_TO_RECORD = 60;
  const DEFAULT_REDIRECT_PATH = "/posture/controls";
  const { isMSSP, rerenderKey } = useMSSP();

  const {
    isScanning: bootstrapIsScanning,
    taskId: bootstrapTaskId,
    isSuccess: bootstrapIsSuccess,
    customerFeatureFlags,
    customerFeatureFlagsProcessed,
  } = useContext(BootstrapContext);

  useEffect(() => {
    const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;
    if (mixpanelToken && process.env.REACT_APP_MIXPANEL_ENABLE_REPLAY) {
      mixpanel.init(mixpanelToken, {
        autocapture: true,
        record_sessions_percent: process.env.REACT_APP_MIXPANEL_ENABLE_REPLAY
          ? MIXPANEL_PERCENTAGE_TO_RECORD
          : 0,
        record_heatmap_data: true,
      });
    }
  }, []);

  useEffect(() => {
    if (bootstrapIsSuccess) {
      setIsScanning(bootstrapIsScanning);
      setTaskId(bootstrapTaskId);
      setIsLoading(bootstrapIsScanning);
    }
  }, [bootstrapIsScanning, bootstrapTaskId, bootstrapIsSuccess]);

  useEffect(() => {
    let intervalId;

    if (isScanning) {
      intervalId = setInterval(() => {
        getScanStatus(taskId)
          .then((result) => {
            const status = result.data;
            if (
              status === TaskStatus.COMPLETED ||
              status === TaskStatus.FAILED ||
              status === TaskStatus.CANCELLED
            ) {
              setIsScanning(false);
              clearInterval(intervalId);
              setIsLoading(false);
              setTaskId(null);
            }
            if (status === TaskStatus.COMPLETED_PARTIAL) {
              setLastScanTime(Date.now());
              queryClient.invalidateQueries({ queryKey: controlKeys.all });
              Toast.success("Controls scan completed");
              setTimeout(() => {
                Toast.info("Scenarios scan in progress...");
              }, delayBetweenToasts); // short delay for better UX
            }
            if (status === TaskStatus.COMPLETED) {
              setLastScanTime(Date.now());
              setScanFinished(true);
              Toast.success("Scan completed successfully");
            }
            if (status === TaskStatus.FAILED) {
              setScanFinished(true);
              Toast.error("Scan failed");
            }
            if (status === TaskStatus.CANCELLED) {
              setScanFinished(true);
              Toast.error("Scan cancelled because another scan is in process");
            }
          })
          .catch(() => {
            setIsScanning(false);
            clearInterval(intervalId);
            setIsLoading(false);
            setTaskId(null);
            setScanFinished(true);
          });
      }, intervalTimeInMilliseconds);
    }

    return () => clearInterval(intervalId);
  }, [isScanning]);

  useEffect(() => {
    // Extract tracking parameters from URL if present
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get(QueryParam.source);
    const notificationType = searchParams.get(QueryParam.notificationType);
    const ctaType = searchParams.get(QueryParam.ctaType);

    // Create tracking message with additional parameters if they exist
    let message = "Navigation to " + location.pathname;
    if (source) message += ` from ${source}`;
    if (notificationType) message += ` type=${notificationType}`;
    if (ctaType) message += ` cta=${ctaType}`;

    trackEvent(message).then();
  }, [location.pathname, location.search]);

  // use storage to show last sidebar on page refresh
  useEffect(() => {
    localStorage.setItem(
      TOPBAR_STORAGE_KEY,
      JSON.stringify(selectedTopbarIndex)
    );
  }, [selectedTopbarIndex]);

  const {
    permitted: userIsPermittedToViewAllPages,
    isSuccess: userPermissionIsSuccess,
  } = useCheckUserPermission(PLATFORM_USER_ACTIONS.VIEW_ALL_PAGES);

  const appScenes = getAppScenes(
    userIsPermittedToViewAllPages,
    customerFeatureFlagsProcessed?.aiPostureDashboardEnabled
  );

  // create all routes from navigation
  const routes = appScenes.map((scene) => {
    if (!userPermissionIsSuccess) return null;

    const shouldRedirectScene = scene.notPermitted && userPermissionIsSuccess;
    if (shouldRedirectScene) {
      return (
        <Route
          key={scene.path}
          path={scene.path}
          element={<Navigate to={DEFAULT_REDIRECT_PATH} replace />}
        />
      );
    } else if (scene.path === "/" || !scene.pages) {
      return (
        <Route key={scene.path} path={scene.path} element={scene.element} />
      );
    } else {
      return (
        <>
          {scene.pages.map((page) => {
            const shouldRedirectPage =
              page.notPermitted && userPermissionIsSuccess;
            return (
              page.enabled && (
                <Route
                  key={page.path}
                  path={page.path}
                  element={
                    shouldRedirectPage ? (
                      <Navigate to={DEFAULT_REDIRECT_PATH} replace />
                    ) : (
                      page.element
                    )
                  }
                />
              )
            );
          })}
          {/* redirect the scene path to the dashboard */}
          <Route
            key={scene.path}
            path={scene.path}
            element={<Navigate key={scene.path} to={"/"} />}
          ></Route>
        </>
      );
    }
  });

  return (
    <EnvFeaturesProvider>
      <CustomerFeatureFlagsProvider>
        <POCProvider>
          <OnboardingProvider>
            <OnboardingAutoOpenWrapper>
              <NotificationContext.Provider
                value={{
                  isLoading,
                  isScanning,
                  taskId,
                  lastScanTime,
                  scanFinished,
                  setIsLoading,
                  setIsScanning,
                  setTaskId,
                  setLastScanTime,
                }}
              >
                <ColorModeContext.Provider value={colorMode}>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />

                    <div className="app" key={rerenderKey}>
                      <ProSidebarProvider>
                        <AppSidebar items={appScenes} />
                        <main className="content">
                          <Box display="flex" flexDirection="column">
                            {isMSSP && <MSSPHeader />}

                            <Routes>
                              {/* add routes for scene pages */}
                              {routes}

                              <Route
                                key={"frameworks"}
                                path="/frameworks"
                                element={<FrameworksView />}
                              />
                              {customerFeatureFlagsProcessed?.aiPostureDashboardEnabled && (
                                <Route
                                  key={"ai-posture"}
                                  path="/ai-posture"
                                  element={<AIDashboard />}
                                />
                              )}
                              <Route
                                key={"frameworks-details"}
                                path="/frameworks/:framework"
                                element={<FrameworksView />}
                              />
                              <Route
                                key={"integration"}
                                path="/inventory/integrations/:integrationName"
                                element={<IntegrationPage />}
                              />
                              <Route
                                key={"scan"}
                                path="/inventory/redirectgithub"
                                element={<HandleGitHubRedirect />}
                              />
                              <Route
                                key={"support"}
                                path="/settings/support"
                                element={<SupportDevTools />}
                              />

                              <Route
                                key={"onprem"}
                                path="/settings/connector"
                                element={<OnPremConfigurationView />}
                              />
                              <Route
                                key={"api-docs"}
                                path="/api/docs"
                                element={<ApiDocs />}
                              />
                              <Route
                                key={"steps"}
                                path="/posture/steps"
                                element={<StepsView />}
                              />
                              <Route
                                key={"onboarding"}
                                path="/onboarding/*"
                                element={<Onboarding />}
                              />
                              <Route
                                key={"component-docs"}
                                path="/documentation"
                                element={<ComponentDocs />}
                              />
                            </Routes>
                          </Box>
                        </main>
                      </ProSidebarProvider>
                      <OnboardingModal />
                      <IntegrationSuccessModal />
                    </div>
                  </ThemeProvider>
                  <ToastContainer />
                </ColorModeContext.Provider>
              </NotificationContext.Provider>
            </OnboardingAutoOpenWrapper>
          </OnboardingProvider>
        </POCProvider>
      </CustomerFeatureFlagsProvider>
    </EnvFeaturesProvider>
  );
}

export default withAuthenticationRequired(App, {
  onRedirecting: () => <Box />,
});
